import { create } from 'zustand';
import { ApiService } from '@/shared/services/api.service';
import { GlobalAudioNotificationService } from '@host/services/audio-notification.service';

// Push the user's mute/volume choice into the audio service, which every sound in the
// app already plays through. Before NGE-988 only the chat handler read these values, so
// the switch and slider silently did nothing for office knocks, door locks, or room and
// guest alerts — and the latter were hardcoded to 50% volume regardless.
const applyToAudioService = (enabled: boolean, volumePercent: number) => {
  GlobalAudioNotificationService.setVolume(volumePercent);
  if (enabled) GlobalAudioNotificationService.unmute();
  else GlobalAudioNotificationService.mute();
};

export type DisplayMode = 'extended' | 'medium' | 'condensed';
export type SortBy = 'activity' | 'name';

const RC_PREFS_SLUG = 'rc-chat';
export const SIDEBAR_MIN_WIDTH = 160;
export const SIDEBAR_MAX_WIDTH = 480;

const LAST_ROOM_KEY = 'rc-last-room-id';
const loadLastRoomId = (): string | null => {
  try {
    return localStorage.getItem(LAST_ROOM_KEY);
  } catch {
    return null;
  }
};
const saveLastRoomId = (id: string | null) => {
  try {
    if (id) localStorage.setItem(LAST_ROOM_KEY, id);
    else localStorage.removeItem(LAST_ROOM_KEY);
  } catch {}
};

const DEFAULTS = {
  displayMode: 'extended' as DisplayMode,
  showAvatars: true,
  sortBy: 'activity' as SortBy,
  groupByUnread: true,
  groupByFavorites: true,
  groupByTypes: true,
  sidebarWidth: 220,
  sidebarCollapsed: false,
  panelWidth: 600,
  lastRoomId: loadLastRoomId(),
  soundNotificationsEnabled: true,
  soundNotificationVolume: 100,
};

interface DisplayPrefsState {
  displayMode: DisplayMode;
  showAvatars: boolean;
  sortBy: SortBy;
  groupByUnread: boolean;
  groupByFavorites: boolean;
  groupByTypes: boolean;
  sidebarWidth: number;
  sidebarCollapsed: boolean;
  panelWidth: number;
  lastRoomId: string | null;
  // True once loadFromServer settles, so "nothing to restore" isn't decided from the
  // localStorage copy alone (a new browser only has the server's lastRoomId).
  prefsLoaded: boolean;
  soundNotificationsEnabled: boolean;
  soundNotificationVolume: number;
  setDisplayMode: (mode: DisplayMode) => void;
  setShowAvatars: (show: boolean) => void;
  setSortBy: (sort: SortBy) => void;
  setGroupByUnread: (v: boolean) => void;
  setGroupByFavorites: (v: boolean) => void;
  setGroupByTypes: (v: boolean) => void;
  setSidebarWidth: (width: number) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setPanelWidth: (width: number) => void;
  setLastRoomId: (id: string | null) => void;
  setSoundNotificationsEnabled: (enabled: boolean) => void;
  setSoundNotificationVolume: (volume: number) => void;
  loadFromServer: () => Promise<void>;
  saveToServer: () => Promise<void>;
}

let _saveTimer: ReturnType<typeof setTimeout> | null = null;
const scheduleSave = (saveFn: () => void) => {
  if (_saveTimer) clearTimeout(_saveTimer);
  _saveTimer = setTimeout(saveFn, 800);
};

export const useDisplayPrefsStore = create<DisplayPrefsState>()((set, get) => ({
  ...DEFAULTS,
  prefsLoaded: false,

  setDisplayMode: (displayMode) => {
    set({ displayMode });
    scheduleSave(() => get().saveToServer());
  },
  setShowAvatars: (showAvatars) => {
    set({ showAvatars });
    scheduleSave(() => get().saveToServer());
  },
  setSortBy: (sortBy) => {
    set({ sortBy });
    scheduleSave(() => get().saveToServer());
  },
  setGroupByUnread: (groupByUnread) => {
    set({ groupByUnread });
    scheduleSave(() => get().saveToServer());
  },
  setGroupByFavorites: (groupByFavorites) => {
    set({ groupByFavorites });
    scheduleSave(() => get().saveToServer());
  },
  setGroupByTypes: (groupByTypes) => {
    set({ groupByTypes });
    scheduleSave(() => get().saveToServer());
  },
  setSidebarWidth: (width) => {
    set({ sidebarWidth: Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, width)) });
    scheduleSave(() => get().saveToServer());
  },
  setSidebarCollapsed: (sidebarCollapsed) => {
    set({ sidebarCollapsed });
    scheduleSave(() => get().saveToServer());
  },
  setPanelWidth: (width) => {
    set({ panelWidth: Math.max(300, Math.min(1000, width)) });
    scheduleSave(() => get().saveToServer());
  },
  setLastRoomId: (lastRoomId) => {
    saveLastRoomId(lastRoomId);
    set({ lastRoomId });
    scheduleSave(() => get().saveToServer());
  },
  setSoundNotificationsEnabled: (soundNotificationsEnabled) => {
    set({ soundNotificationsEnabled });
    applyToAudioService(soundNotificationsEnabled, get().soundNotificationVolume);
    scheduleSave(() => get().saveToServer());
  },
  setSoundNotificationVolume: (volume) => {
    const soundNotificationVolume = Math.max(0, Math.min(100, volume));
    set({ soundNotificationVolume });
    applyToAudioService(get().soundNotificationsEnabled, soundNotificationVolume);
    scheduleSave(() => get().saveToServer());
  },

  loadFromServer: async () => {
    // Always start from defaults so a new/different user never sees stale state
    set({ ...DEFAULTS, prefsLoaded: false });
    try {
      const res = await ApiService.get<{ settings?: Record<string, unknown> }>(
        `user-org-preferences/app-settings/${RC_PREFS_SLUG}`
      );
      const s = res?.settings;
      if (!s) return;
      const update: Partial<typeof DEFAULTS> = {};
      if (typeof s.displayMode === 'string') update.displayMode = s.displayMode as DisplayMode;
      if (typeof s.showAvatars === 'boolean') update.showAvatars = s.showAvatars;
      if (typeof s.sortBy === 'string') update.sortBy = s.sortBy as SortBy;
      if (typeof s.groupByUnread === 'boolean') update.groupByUnread = s.groupByUnread;
      if (typeof s.groupByFavorites === 'boolean') update.groupByFavorites = s.groupByFavorites;
      if (typeof s.groupByTypes === 'boolean') update.groupByTypes = s.groupByTypes;
      if (typeof s.sidebarWidth === 'number')
        update.sidebarWidth = Math.max(
          SIDEBAR_MIN_WIDTH,
          Math.min(SIDEBAR_MAX_WIDTH, s.sidebarWidth)
        );
      if (typeof s.sidebarCollapsed === 'boolean') update.sidebarCollapsed = s.sidebarCollapsed;
      if (typeof s.panelWidth === 'number')
        update.panelWidth = Math.max(300, Math.min(1000, s.panelWidth));
      if (typeof s.soundNotificationsEnabled === 'boolean')
        update.soundNotificationsEnabled = s.soundNotificationsEnabled;
      if (typeof s.soundNotificationVolume === 'number')
        update.soundNotificationVolume = Math.max(0, Math.min(100, s.soundNotificationVolume));
      // Server value wins; fall back to localStorage so refreshes within the debounce window don't lose the room.
      if (typeof s.lastRoomId === 'string') update.lastRoomId = s.lastRoomId;
      else {
        const local = loadLastRoomId();
        if (local) update.lastRoomId = local;
      }
      if (Object.keys(update).length > 0) set(update);
    } catch {
      // keep defaults on error
    } finally {
      // Mirror whatever we settled on (server values or defaults) into the audio service.
      applyToAudioService(get().soundNotificationsEnabled, get().soundNotificationVolume);
      set({ prefsLoaded: true });
    }
  },

  saveToServer: async () => {
    const s = get();
    try {
      await ApiService.put(`user-org-preferences/app-settings/${RC_PREFS_SLUG}`, {
        settings: {
          displayMode: s.displayMode,
          showAvatars: s.showAvatars,
          sortBy: s.sortBy,
          groupByUnread: s.groupByUnread,
          groupByFavorites: s.groupByFavorites,
          groupByTypes: s.groupByTypes,
          sidebarWidth: s.sidebarWidth,
          sidebarCollapsed: s.sidebarCollapsed,
          panelWidth: s.panelWidth,
          lastRoomId: s.lastRoomId,
          soundNotificationsEnabled: s.soundNotificationsEnabled,
          soundNotificationVolume: s.soundNotificationVolume,
        },
      });
    } catch {
      // silent — UI already reflects the user's intent
    }
  },
}));
